import { MultiColumnCard } from "components/Card/MultiColumnCard";
import { Select, TextInput } from "components/inputs";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { CustomService } from "types/DataTypes";

import type { OptionType } from "components/inputs";

interface CustomServiceRowProps {
  id: string;
  name: string;
  service: string;
  onFieldUpdate: (rowId: string, field: string, value: string) => void;
  constants: {
    SERVICE_TYPE: OptionType[];
  };
  availableServices: OptionType[];
}

const CustomServiceRow = memo(
  ({
    id,
    name: initialName,
    service: initialService,
    onFieldUpdate,
    constants,
    availableServices,
  }: CustomServiceRowProps) => {
    const { control } = useFormContext();
    const [localName, setLocalName] = useState(initialName);

    const handleNameBlur = useCallback(() => {
      if (localName !== initialName) {
        onFieldUpdate(id, "name", localName);
      }
    }, [id, localName, initialName, onFieldUpdate]);

    const handleServiceChange = useCallback(
      (option: OptionType) => {
        onFieldUpdate(id, "service", String(option.value));
      },
      [id, onFieldUpdate],
    );

    return (
      <>
        <figure style={{ flex: 1 }}>
          <Controller
            name={`custom_services.${id}.service`}
            control={control}
            render={({ field: { value } }) => (
              <Select
                name="service"
                className="CustomServices__Row__Select"
                options={availableServices}
                value={constants?.SERVICE_TYPE?.find(
                  (option) =>
                    option.value === value || option.value === initialService,
                )}
                onChange={handleServiceChange}
              />
            )}
          />
        </figure>
        <figure style={{ flex: 1 }}>
          <TextInput
            name="name"
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleNameBlur}
          />
        </figure>
      </>
    );
  },
);

CustomServiceRow.displayName = "CustomServiceRow";

interface CustomServicesFormProps {
  onSave: (field: string, value: unknown) => Promise<void>;
  constants: {
    SERVICE_TYPE: OptionType[];
  };
}

export const CustomServicesForm = ({
  onSave,
  constants,
}: CustomServicesFormProps) => {
  const { getValues, setValue, watch } = useFormContext();
  const watchedCustomServices = watch("custom_services");

  const [localRows, setLocalRows] = useState<CustomService[]>(() => {
    const initialRows = getValues("custom_services");
    if (Array.isArray(initialRows)) {
      return initialRows.map((row: CustomService) => ({
        ...row,
        id: row.id || crypto.randomUUID(),
      }));
    }
    return [];
  });

  useEffect(() => {
    if (watchedCustomServices && Array.isArray(watchedCustomServices)) {
      setLocalRows(
        watchedCustomServices.map((row: CustomService) => ({
          ...row,
          id: row.id || crypto.randomUUID(),
        })),
      );
    }
  }, [watchedCustomServices]);

  const availableServices = useMemo(() => {
    const existingServices = localRows.map((row) => row.service);
    return constants?.SERVICE_TYPE?.filter(
      (serviceType) => !existingServices.includes(String(serviceType.value)),
    );
  }, [localRows, constants?.SERVICE_TYPE]);

  const handleNewRow = useCallback(() => {
    const newId = crypto.randomUUID();
    setLocalRows((prev) => [
      ...prev,
      {
        id: newId,
        name: "",
        service: "",
      },
    ]);
  }, []);

  const handleFieldUpdate = useCallback(
    (rowId: string, field: string, value: string) => {
      setLocalRows((prev) => {
        const newRows = prev.map((row) => {
          const rowWithId = { ...row, id: row.id || crypto.randomUUID() };
          const isTargetRow = rowWithId.id === rowId;
          return isTargetRow ? { ...rowWithId, [field]: value } : rowWithId;
        });

        const updatedRow = newRows.find((r) => r.id === rowId);

        if (updatedRow?.name && updatedRow?.service) {
          const completeRows = newRows
            .filter((r) => r.name && r.service)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
            .map(({ id, ...rest }) => rest);

          setValue("custom_services", completeRows);
          onSave("custom_services", completeRows);
        }

        return newRows;
      });
    },
    [setValue, onSave],
  );

  const handleDeleteRow = useCallback(
    async (row: CustomService) => {
      setLocalRows((prev) => {
        const newRows = prev.filter((r) => {
          const shouldKeep = !(
            r.name === row.name && r.service === row.service
          );
          return shouldKeep;
        });
        setValue("custom_services", newRows);
        onSave("custom_services", newRows);
        return newRows;
      });
    },
    [setValue, onSave],
  );

  return (
    <MultiColumnCard
      columnNames={["Service", "New Name"]}
      heading="Custom Service Names"
      initialRows={localRows}
      onNewRow={handleNewRow}
      onDeleteRow={handleDeleteRow}
      layout="inverse"
      classNames="CustomServices allocation-medical"
      RowComponent={(props) => {
        return (
          <CustomServiceRow
            {...props}
            onFieldUpdate={handleFieldUpdate}
            constants={constants}
            availableServices={availableServices}
          />
        );
      }}
      getRowKey={(row: CustomService) => {
        return String(row.id);
      }}
    />
  );
};
